import { setGetFirstValue } from '@seaber/common/lib/collections';
import { type ClientId } from '../../core/ClientId';
import {
  isCargoEventTimeData,
  isDischargeEventTimeData,
  isLoadEventTimeData,
  type TimeData,
} from '../../core/TimeData';
import {
  createDependencyAccessor,
  type PnlProcessorContext,
} from '../context/context';
import { PnlProcessor, type PnlProcessorResult } from '../processor';
import { assertDefined } from '@seaber/common/lib/generics';
import { getEventShipmentId } from '../../shipment/shipment';
import { logMissingShipmentId, MISSING_SHIPMENT_ID } from '../../shipment/TCE';

const PROCESSOR_METADATA = { id: 'cost-sharing', dependencies: [] };

export type CostSharingDetails = {
  /** The primary shipment associated with the event */
  eventShipmentId: ClientId | null;
  /** The first loaded shipment of currently on board ones */
  firstLoadedShipment: ClientId | null;
  /** Defined if there are currently no shipments on board and there are events in the future  */
  nextLoadedShipment: ClientId | null;
  /** Last shipment that was discharged */
  lastDischargedShipment: ClientId | null;
  /** The primary shipment associated with a possible voyage leg before the event */
  legShipmentId: ClientId | null;
  /** Shipments currently on board */
  shipmentsOnBoard: Set<ClientId>;
};

export interface CostSharingContext {
  costSharingDetailsByTd: Map<TimeData, CostSharingDetails>;
}

export const getCostSharingContext =
  createDependencyAccessor<CostSharingContext>(PROCESSOR_METADATA.id);

/**
 * Returns the shipment associated with a given timedata's event

 * Note: The leg part of the timedata might belong to another shipment
 */
export function getTimeDataEventShipmentId(
  context: PnlProcessorContext,
  td: TimeData,
): ClientId | null {
  const shared = getCostSharingContext(context);
  const details = shared.costSharingDetailsByTd.get(td);
  return details?.eventShipmentId ?? null;
}

export class CostSharingProcessor extends PnlProcessor<undefined> {
  private readonly state: {
    previousDischargePortCallId: ClientId | null;
    /** The last shipment that was discharged */
    lastDischargedShipment: ClientId | null;
    /** Count of orders loaded for each shipment on board */
    ordersOnBoardByShipment: Map<ClientId, number>;
    /** Buffer for events that we cannot yet figure out the correct shipment id for */
    pendingTimeData: TimeData[];
    /** Shipments currently on board */
    shipmentsOnBoard: Set<ClientId>;
  } = {
    previousDischargePortCallId: null,
    lastDischargedShipment: null,
    ordersOnBoardByShipment: new Map(),
    pendingTimeData: [],
    shipmentsOnBoard: new Set(),
  };

  constructor(context: PnlProcessorContext) {
    super(PROCESSOR_METADATA, context);

    // Initialize shared context
    context.shared[PROCESSOR_METADATA.id] = {
      costSharingDetailsByTd: new Map(),
    };
  }

  private result: PnlProcessorResult = {
    costs: undefined,
    errors: [],
    income: undefined,
  };

  processEvent(td: TimeData): void {
    if (isLoadEventTimeData(td)) {
      const shipmentId = getEventShipmentId(td.event) ?? MISSING_SHIPMENT_ID;

      // Shipment id is expected to be available for an order on the timeline
      if (shipmentId === MISSING_SHIPMENT_ID) {
        logMissingShipmentId(td.event.order);
      }

      this.state.shipmentsOnBoard.add(shipmentId);

      const ordersOnBoardByShipment =
        this.state.ordersOnBoardByShipment.get(shipmentId) ?? 0;

      this.state.ordersOnBoardByShipment.set(
        shipmentId,
        ordersOnBoardByShipment + 1,
      );
    }

    // If a shipment has been discharged but we have not yet departed the
    // event should be associated with the last discharged shipment
    const isAtPreviousDischargePort =
      this.state.previousDischargePortCallId &&
      td.event.portCallId === this.state.previousDischargePortCallId;

    // If we have not encountered a shipment yet and we have left the previous
    // discharge port, queue event until we encounter the next load event
    if (this.state.shipmentsOnBoard.size < 1 && !isAtPreviousDischargePort) {
      this.state.pendingTimeData.push(td);
      return;
    }

    // Process all pending time data that were buffered before encoutering a shipment
    for (const pendingTd of this.state.pendingTimeData) {
      this.updateSharedContext(pendingTd, true);
    }
    this.state.pendingTimeData = [];

    this.updateSharedContext(td, false);

    if (isDischargeEventTimeData(td)) {
      const shipmentId = getEventShipmentId(td.event) ?? MISSING_SHIPMENT_ID;

      this.state.lastDischargedShipment = shipmentId;

      // Update tracking of loaded shipments
      let orderCountByShipment =
        this.state.ordersOnBoardByShipment.get(shipmentId);

      assertDefined(orderCountByShipment, 'Count expected to be available');

      orderCountByShipment -= 1;
      this.state.ordersOnBoardByShipment.set(shipmentId, orderCountByShipment);

      if (orderCountByShipment === 0) {
        this.state.shipmentsOnBoard.delete(shipmentId);
      }

      this.state.previousDischargePortCallId = td.event.portCallId;
    }
  }

  finalize(): void {
    // Any dangling events at the end of the schedule should be associated with
    // the last shipment
    for (const pendingTd of this.state.pendingTimeData) {
      this.updateSharedContext(pendingTd, false);
    }
  }

  getResult() {
    return this.result;
  }

  private getSharedContext(): CostSharingContext {
    return this.context.shared[PROCESSOR_METADATA.id] as CostSharingContext;
  }

  private updateSharedContext(td: TimeData, isPending: boolean): void {
    const shared = this.getSharedContext();

    const firstShipmentOnBoard =
      setGetFirstValue(this.state.shipmentsOnBoard) ?? null;

    // If the shipment was buffered the first loaded shipment available via state
    // is in its future and not actually on board at the time of the event
    const firstLoadedShipment = isPending ? null : firstShipmentOnBoard;
    const nextLoadedShipment = isPending ? firstShipmentOnBoard : null;

    const lastDischargedShipment = this.state.lastDischargedShipment ?? null;

    const eventShipmentId = isCargoEventTimeData(td)
      ? getEventShipmentId(td.event)
      : firstLoadedShipment || nextLoadedShipment || lastDischargedShipment;

    const legShipmentId =
      firstLoadedShipment || nextLoadedShipment || lastDischargedShipment;

    shared.costSharingDetailsByTd.set(td, {
      eventShipmentId,
      firstLoadedShipment,
      lastDischargedShipment,
      legShipmentId,
      nextLoadedShipment,
      shipmentsOnBoard: new Set(this.state.shipmentsOnBoard),
    });
  }
}
